// Shared mappers: legacy @shared/models entities → thin booking-v2 option DTOs.
// Extracted from berth-step / person-step so the step components AND the
// BookingEntryService / scheduler dispatcher build the exact same option shapes
// from a single source of truth (no divergent owner-resolution rules).
//
// This file imports only models + DTOs — never the facade or step components —
// to stay free of circular imports.

import { resolveBoatArea } from '@shared/functions/resolve-boat-area';
import { resolveBoatLength } from '@shared/functions/resolve-boat-length';
import { Boat } from '@shared/models/boat';
import { Company } from '@shared/models/company';
import { Person } from '@shared/models/person';
import { TenantRelation } from '@shared/models/tenant-relation';

import {
    BookingV2BerthOption,
    BookingV2BoatDimensions,
    BookingV2BoatOption,
    BookingV2OwnerOption,
    BookingV2PersonOption,
    BookingV2RelationOption,
    BookingV2RelationProduct,
} from '../dtos/booking-v2.dtos';

// Accepts both the raw API berth (search results expose `pier.handle`) and a
// hydrated `Berth` model instance (exposes `pierHandle` directly) — so the same
// mapper serves the berth-step list and the entry-service prefill.
export function toBerthOption(raw: {
    id: number;
    handle: string;
    pier?: { id?: number; handle?: string } | null;
    pierHandle?: string | null;
    length: number;
    width: number;
    maxDraft: number;
}): BookingV2BerthOption {
    return {
        id: raw.id,
        handle: raw.handle,
        pierHandle: raw.pier?.handle ?? raw.pierHandle ?? null,
        pierId: raw.pier?.id ?? null,
        length: raw.length,
        width: raw.width,
        maxDraft: raw.maxDraft,
    };
}

export function toBoatOption(raw: Boat): BookingV2BoatOption {
    const ownerLabel =
        raw.owner?.fullName ||
        raw.ownerCompany?.fullName ||
        null;
    // `/boats/index` eager-loads `owner`/`ownerCompany` (id + name only), so
    // the owner identity travels with the boat for the person-step prefill.
    // Boats nested under an owner search result carry no owner sub-graph —
    // those get the owner attached via `selectOwnerBoat()` instead.
    const ownerId = raw.owner?.id ?? raw.ownerCompany?.id ?? null;
    const ownerKind: 'person' | 'company' | null =
        raw.owner?.id != null ? 'person' : (raw.ownerCompany?.id != null ? 'company' : null);
    return {
        id: raw.id,
        name: raw.name || null,
        licensePlate: raw.licensePlate || null,
        // Hafen zeigt/rechnet immer mit LüA; length als Fallback wenn LüA leer.
        length: resolveBoatLength(raw),
        width: raw.width,
        depth: raw.depth,
        sqm: resolveBoatArea(raw),
        ownerLabel,
        ownerId,
        ownerKind,
    };
}

// Dimension-only boat (`properties` mode / map search without a picked boat):
// filters berths by fit and shows in the selection, but blocks Save until a real
// boat replaces it — see BookingV2CommitService.blockedByManualBoat.
export function toManualBoatOption(dimensions: BookingV2BoatDimensions): BookingV2BoatOption {
    return {
        id: -1,
        name: null,
        licensePlate: null,
        length: dimensions.length,
        width: dimensions.width,
        depth: dimensions.depth,
        // No area: the typed length is the LüA, while the billing engine resolves a boat's m²
        // from the hull length. Offering a quantity here that the invoice cannot reproduce is
        // worse than offering none. (#6944)
        sqm: null,
        ownerLabel: null,
        origin: 'manual',
    };
}

export function toPersonOwner(raw: Person): BookingV2OwnerOption {
    return {
        id: raw.id,
        kind: 'person',
        fullName: raw.fullName || `${raw.firstName ?? ''} ${raw.lastName ?? ''}`.trim(),
        boats: (raw.boats || []).map(toBoatOption),
    };
}

export function toCompanyOwner(raw: Company): BookingV2OwnerOption {
    return {
        id: raw.id,
        kind: 'company',
        fullName: raw.fullName || raw.name || '',
        boats: (raw.boats || []).map(toBoatOption),
    };
}

export function toPersonOption(raw: Person): BookingV2PersonOption {
    return {
        id: raw.id,
        kind: 'person',
        displayName: raw.fullName || `${raw.firstName ?? ''} ${raw.lastName ?? ''}`.trim(),
        contactPerson: null,
        address: raw.fullAddress || null,
        phone: raw.phone || null,
        mail: raw.mail || null,
        origin: 'existing',
    };
}

export function toCompanyOption(raw: Company): BookingV2PersonOption {
    return {
        id: raw.id,
        kind: 'company',
        displayName: raw.fullName || raw.name || '',
        contactPerson: raw.companyContacts?.[0]?.personName || null,
        address: raw.fullAddress || null,
        phone: raw.phone || null,
        mail: raw.mail || null,
        origin: 'existing',
    };
}

export function toRelationOption(raw: TenantRelation): BookingV2RelationOption {
    return {
        id: raw.id,
        name: raw.name,
        note: raw.note || null,
        hexColor: raw.hexColor || null,
        hexBgColor: raw.hexBgColor || null,
        payableOption: raw.payableOption ?? null,
    };
}

// The relation's attached products → read-only preview lines. The pivot quantity
// survives on `Product.quantity`; the custom pivot title is dropped by the model,
// so the product name is used.
export function toRelationProducts(raw: TenantRelation): BookingV2RelationProduct[] {
    return (raw.products || []).map(p => ({
        productId: p.id,
        name: p.name,
        quantity: p.quantity || 1,
        unitId: p.unit?.id ?? null,
        unitLabelKey: p.unit?.translationKey ?? null,
        unitUniqueName: p.unit?.uniqueName ?? null,
        taxRateId: p.taxRate?.id ?? null,
        price: p.price,
        vatValue: p.taxRate?.value ?? 0,
        hasDynamicPrice: !!p.hasDynamicPrice,
        priceRuleShape: {
            ruleSetCount: p.priceRuleMap?.ruleSets?.length ?? 0,
            groupCount: p.priceRuleMap?.groups != null ? p.priceRuleMap.groups.length : null,
        },
        tenantRelationCriteriaIds: p.priceRuleMap?.getTenantRelationCriteriaIds?.() ?? [],
    }));
}

// Distinct entity ids referenced by the dynamic-price rules of the relation's
// products (boat / berth / season / pier / winterStorage / …), via the existing
// `priceRuleMap.getRequirements()`. Drives which pickers/hints the wizard shows.
export function relationRuleRequirements(raw: TenantRelation): string[] {
    return [...new Set(
        (raw.products || [])
            .filter(p => p.hasDynamicPrice)
            .flatMap(p => p.priceRuleMap?.getRequirements() ?? []),
    )];
}

// Relation option carrying its products preview (for the relation card + the
// Rechnungsversand step) and the aggregated rule requirements. Bare
// `toRelationOption` stays minimal for other callers.
export function toRelationOptionWithProducts(raw: TenantRelation): BookingV2RelationOption {
    return {
        ...toRelationOption(raw),
        products: toRelationProducts(raw),
        requiredEntities: relationRuleRequirements(raw),
    };
}
